import { clearPendingRevitOperation } from '../../../api/grpc/operationProgressClient';
import {
  describeConnectorExecutionMode,
  formatConnectorTargetLabel,
} from './operationIdentity';
import {
  type ActiveConnectorOperationUiState,
  type ParameterAdditionResult,
} from './models';
import {
  type ConnectorExecutionContext,
  type PendingConnectorRecoveryDisposition,
} from './pendingConnectorRecovery';

export type PendingOperationProbeStatus = 'live' | 'terminal' | 'lost';

/**
 * Seams the arbitration needs from the hosting hook. Probe/track are optional
 * so legacy core stubs without them degrade to the fail-closed 'lost' path.
 */
export interface PendingConnectorArbitrationDeps {
  probePendingOperation?: (
    operationId: string,
  ) => Promise<PendingOperationProbeStatus>;
  trackPendingOperationToTerminal?: (
    operationId: string,
  ) => Promise<{ terminal: boolean; lost: boolean }>;
  showWarning: (message: string) => void;
}

type BlockedDisposition = Exclude<
  PendingConnectorRecoveryDisposition,
  { kind: 'none' } | { kind: 'exact-match' }
>;

export interface ConnectorOperationUiSnapshot {
  activeOperation: ActiveConnectorOperationUiState;
  selectionBlockedByMismatch: boolean;
}

const PENDING_CONNECTOR_UPDATE_TITLE = 'Finishing the previous connector update';

/**
 * Builds the typed operation state the UI shows while an invocation is live.
 * Pure: every description comes from the disposition record or the frozen
 * context captured at click time — never from live configuration values.
 */
export function buildConnectorOperationUiSnapshot(
  disposition: PendingConnectorRecoveryDisposition,
  frozenContext: ConnectorExecutionContext,
): ConnectorOperationUiSnapshot {
  switch (disposition.kind) {
    case 'none':
      return {
        activeOperation: {
          owner: frozenContext.owner,
          target: {
            destination: frozenContext.destination,
            bindingKind: frozenContext.bindingKind,
          },
          description: describeConnectorExecutionMode(
            frozenContext.destination,
            frozenContext.bindingKind,
          ),
        },
        selectionBlockedByMismatch: false,
      };
    case 'exact-match':
      return {
        activeOperation: {
          owner: disposition.record.owner,
          target: disposition.record.connectorTarget,
          description: PENDING_CONNECTOR_UPDATE_TITLE,
        },
        selectionBlockedByMismatch: false,
      };
    case 'mode-mismatch':
      return {
        activeOperation: {
          owner: disposition.record.owner,
          target: disposition.record.connectorTarget,
          description: PENDING_CONNECTOR_UPDATE_TITLE,
        },
        selectionBlockedByMismatch: true,
      };
    case 'project-or-document-mismatch':
      return {
        activeOperation: {
          owner: disposition.record.owner,
          target: disposition.record.connectorTarget,
          description: PENDING_CONNECTOR_UPDATE_TITLE,
        },
        selectionBlockedByMismatch: true,
      };
    case 'foreign-owner':
      return {
        activeOperation: {
          owner: disposition.record.owner,
          target: null,
          description: 'Waiting for another Revit operation to finish',
        },
        selectionBlockedByMismatch: true,
      };
    case 'legacy-or-malformed':
      return {
        activeOperation: {
          owner: 'unknown',
          target: null,
          description: 'Resolving an outdated Revit operation record',
        },
        selectionBlockedByMismatch: true,
      };
  }
}

interface ProbeBlockCopy {
  live: string;
  cleared: string;
  blocked: string;
}

const blockedResult = (error: string): ParameterAdditionResult => ({
  success: false,
  addedCount: 0,
  blockedByPendingOperation: true,
  error,
});

const probeAndBlock = async (
  operationId: string,
  deps: PendingConnectorArbitrationDeps,
  copy: ProbeBlockCopy,
): Promise<ParameterAdditionResult> => {
  const status = deps.probePendingOperation
    ? await deps.probePendingOperation(operationId)
    : 'lost';
  if (status === 'live') {
    deps.showWarning(copy.live);
  } else {
    clearPendingRevitOperation(operationId);
    deps.showWarning(copy.cleared);
  }

  return blockedResult(copy.blocked);
};

const blockOnModeMismatch = async (
  record: Extract<BlockedDisposition, { kind: 'mode-mismatch' }>['record'],
  deps: PendingConnectorArbitrationDeps,
): Promise<ParameterAdditionResult> => {
  const previousLabel = record.connectorTarget
    ? formatConnectorTargetLabel(
      record.connectorTarget.destination,
      record.connectorTarget.bindingKind,
    )
    : 'previously configured';
  const tracked = deps.trackPendingOperationToTerminal
    ? await deps.trackPendingOperationToTerminal(record.operationId)
    : { terminal: false, lost: true };
  if (tracked.terminal || tracked.lost) {
    clearPendingRevitOperation(record.operationId);
    deps.showWarning(
      `A previous ${previousLabel} connector parameter operation finished and its record was cleared. Select Apply and continue again to run the selected mode.`,
    );
  } else {
    deps.showWarning(
      `A previous ${previousLabel} connector parameter operation is still running in Revit. Wait for it to finish, then select Apply and continue again.`,
    );
  }

  return blockedResult(
    `A previous ${previousLabel} connector parameter operation blocked this run. Select Apply and continue again.`,
  );
};

/**
 * Handles every pending-record disposition that must not run the current
 * snapshot plan. Always returns a non-success result: cleanup never continues
 * into a new mutation inside the same user invocation.
 */
export async function blockOnMismatchedPendingOperation(
  disposition: BlockedDisposition,
  deps: PendingConnectorArbitrationDeps,
): Promise<ParameterAdditionResult> {
  if (disposition.kind === 'mode-mismatch') {
    return blockOnModeMismatch(disposition.record, deps);
  }

  if (disposition.kind === 'project-or-document-mismatch') {
    return probeAndBlock(disposition.record.operationId, deps, {
      live:
        'A connector parameter operation started in another Revit project or document is still running. Wait for it to finish there, then select Apply and continue again.',
      cleared:
        'A connector parameter operation from another Revit project or document ended. Its stored record was cleared. Select Apply and continue again to run the selected mode.',
      blocked:
        'A connector parameter operation from another Revit project or document blocked this run. Select Apply and continue again.',
    });
  }

  if (disposition.kind === 'foreign-owner') {
    return probeAndBlock(disposition.record.operationId, deps, {
      live:
        'Another Revit parameter operation is still running. Wait for it to finish, then select Apply and continue again.',
      cleared:
        'Another Revit parameter operation ended and its record was cleared. Select Apply and continue again to run the selected connector mode.',
      blocked:
        'Another Revit parameter operation blocked this run. Select Apply and continue again.',
    });
  }

  if (disposition.operationId === null) {
    clearPendingRevitOperation();
    deps.showWarning(
      'A stored Revit operation record could not be read and was removed. Select Apply and continue again to run the selected mode.',
    );
    return blockedResult(
      'A stored Revit operation record could not be read. Select Apply and continue again.',
    );
  }

  return probeAndBlock(disposition.operationId, deps, {
    live:
      'A Revit parameter operation from a previous app version is still running. Wait for it to finish, then select Apply and continue again.',
    cleared:
      'A Revit parameter operation record from a previous app version ended and was cleared. Select Apply and continue again to run the selected mode.',
    blocked:
      'A Revit parameter operation from a previous app version blocked this run. Select Apply and continue again.',
  });
}
